/**
 * EditOpportunityModal Component
 * 
 * Modal for editing opportunity details.
 * Focused on commonly edited fields.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  Modal,
  ScrollView,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, radius, icon, touch } from '../../../shared/tokens/mobile';
import { OpportunityDetail, UpdateOpportunityRequest, OpportunityStatus } from '../../../shared/types/opportunity';
import { styles } from './styles/editOpportunityModalStyles';

const STATUS_OPTIONS: { key: OpportunityStatus; label: string }[] = [
  { key: 'evaluating', label: 'Evaluating' },
  { key: 'under_contract', label: 'Under Contract' },
  { key: 'owned', label: 'Owned' },
  { key: 'selling', label: 'Selling' },
  { key: 'sold', label: 'Sold' },
  { key: 'passed', label: 'Passed' },
];

interface EditOpportunityModalProps {
  visible: boolean;
  opportunity: OpportunityDetail | null;
  onClose: () => void;
  onSave: (data: UpdateOpportunityRequest) => Promise<boolean>;
  saving?: boolean;
}

export function EditOpportunityModal({ 
  visible, 
  opportunity, 
  onClose, 
  onSave, 
  saving 
}: EditOpportunityModalProps) {
  // Form state
  const [name, setName] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [county, setCounty] = useState('');
  const [listingUrl, setListingUrl] = useState('');
  const [askingPrice, setAskingPrice] = useState('');
  const [acreage, setAcreage] = useState('');
  const [status, setStatus] = useState<OpportunityStatus>('evaluating');
  const [error, setError] = useState<string | null>(null);

  // Populate form when opportunity changes
  useEffect(() => {
    if (opportunity) {
      setName(opportunity.name || '');
      setStreetAddress(opportunity.street_address || opportunity.address || '');
      setCity(opportunity.city || '');
      setState(opportunity.state || '');
      setZipCode(opportunity.zip_code || '');
      setCounty(opportunity.county || '');
      setListingUrl(opportunity.listing_url || opportunity.zillow_url || '');
      setAskingPrice(opportunity.asking_price?.toString() || '');
      setAcreage(opportunity.acreage?.toString() || '');
      setStatus(opportunity.status || 'evaluating');
      setError(null);
    }
  }, [opportunity]);

  const handleClose = useCallback(() => {
    setError(null);
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    const data: UpdateOpportunityRequest = {
      name: name.trim(),
      street_address: streetAddress.trim() || null,
      city: city.trim() || null,
      state: state.trim() || null,
      zip_code: zipCode.trim() || null,
      county: county.trim() || null,
      listing_url: listingUrl.trim() || null,
      asking_price: askingPrice ? parseFloat(askingPrice.replace(/,/g, '')) : null,
      acreage: acreage ? parseFloat(acreage) : null,
      status,
    };

    const success = await onSave(data);
    if (success) {
      handleClose();
    }
  }, [name, streetAddress, city, state, zipCode, county, listingUrl, askingPrice, acreage, status, onSave, handleClose]);

  if (!opportunity) return null;

  return (
    <Modal
      visible={visible && !!opportunity}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={handleClose}
    >
      <KeyboardAvoidingView 
        style={styles.container} 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        {/* Header */}
        <View style={styles.header}>
          <Pressable onPress={handleClose} style={styles.closeButton}>
            <Ionicons name="close" size={icon.lg} color={color.textSecondary} />
          </Pressable>
          <Text style={styles.headerTitle}>Edit Opportunity</Text>
          <Pressable 
            onPress={handleSave} 
            style={[styles.saveButton, saving && styles.saveButtonDisabled]}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator size="small" color={color.surface} />
            ) : (
              <Text style={styles.saveButtonText}>Save</Text>
            )}
          </Pressable>
        </View>

        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
          {/* Error */}
          {error && (
            <View style={styles.errorBanner}>
              <Ionicons name="alert-circle" size={icon.md} color={color.error} />
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}

          {/* Name */}
          <View style={styles.field}>
            <Text style={styles.label}>Name *</Text>
            <TextInput
              style={styles.input}
              value={name}
              onChangeText={setName}
              placeholder="Opportunity name"
              placeholderTextColor={color.textMuted}
            />
          </View>

          {/* Status */}
          <View style={styles.field}>
            <Text style={styles.label}>Status</Text>
            <View style={styles.statusGrid}>
              {STATUS_OPTIONS.map((opt) => (
                <Pressable
                  key={opt.key}
                  onPress={() => setStatus(opt.key)}
                  style={[
                    styles.statusButton,
                    status === opt.key && styles.statusButtonActive,
                  ]}
                >
                  <Text style={[
                    styles.statusButtonText,
                    status === opt.key && styles.statusButtonTextActive,
                  ]}>
                    {opt.label}
                  </Text>
                </Pressable>
              ))}
            </View>
          </View>

          {/* Address */}
          <View style={styles.field}>
            <Text style={styles.label}>Street Address</Text>
            <TextInput
              style={styles.input}
              value={streetAddress}
              onChangeText={setStreetAddress}
              placeholder="123 Main St"
              placeholderTextColor={color.textMuted}
            />
          </View>

          <View style={styles.row}>
            <View style={[styles.field, { flex: 2 }]}>
              <Text style={styles.label}>City</Text>
              <TextInput
                style={styles.input}
                value={city}
                onChangeText={setCity}
                placeholder="City"
                placeholderTextColor={color.textMuted}
              />
            </View>
            <View style={[styles.field, { flex: 1 }]}>
              <Text style={styles.label}>State</Text>
              <TextInput
                style={styles.input}
                value={state}
                onChangeText={setState}
                placeholder="NC"
                placeholderTextColor={color.textMuted}
                maxLength={2}
                autoCapitalize="characters"
              />
            </View>
          </View>

          <View style={styles.row}>
            <View style={[styles.field, { flex: 1 }]}>
              <Text style={styles.label}>ZIP Code</Text>
              <TextInput
                style={styles.input}
                value={zipCode}
                onChangeText={setZipCode}
                placeholder="27601"
                placeholderTextColor={color.textMuted}
                keyboardType="number-pad"
                maxLength={10}
              />
            </View>
            <View style={[styles.field, { flex: 1 }]}>
              <Text style={styles.label}>County</Text>
              <TextInput
                style={styles.input}
                value={county}
                onChangeText={setCounty}
                placeholder="Wake"
                placeholderTextColor={color.textMuted}
              />
            </View>
          </View>

          {/* Financial */}
          <View style={styles.row}>
            <View style={[styles.field, { flex: 1 }]}>
              <Text style={styles.label}>Asking Price</Text>
              <TextInput
                style={styles.input}
                value={askingPrice}
                onChangeText={setAskingPrice}
                placeholder="500000"
                placeholderTextColor={color.textMuted}
                keyboardType="numeric"
              />
            </View>
            <View style={[styles.field, { flex: 1 }]}>
              <Text style={styles.label}>Acreage</Text>
              <TextInput
                style={styles.input}
                value={acreage}
                onChangeText={setAcreage}
                placeholder="10.5"
                placeholderTextColor={color.textMuted}
                keyboardType="decimal-pad"
              />
            </View>
          </View>

          {/* Listing URL */}
          <View style={styles.field}>
            <Text style={styles.label}>Listing URL</Text>
            <TextInput
              style={styles.input}
              value={listingUrl}
              onChangeText={setListingUrl}
              placeholder="https://www.zillow.com/..."
              placeholderTextColor={color.textMuted}
              keyboardType="url"
              autoCapitalize="none"
              autoCorrect={false}
            />
          </View>

          <View style={{ height: space.xxl }} />
        </ScrollView>
      </KeyboardAvoidingView>
    </Modal>
  );
}

